import { prefersReducedMotion } from "@/main/util/motionPreference";
import { holdInPageAnchorScroll, pushInPageHistoryEntry } from "@/main/navigation/NavigationHistoryState";
import { isPageInterceptableClick } from "@/main/navigation/NavigationPolicy";

// Opt-in in-page (fragment) anchor scrolling.
//
// Why this exists: on the marketing landing pages the "See how it works" button is a plain
// `<a href="#how-it-works">`. In the iOS Google app in-app browser (GSA) the browser's *native*
// fragment scroll to that section recurses into "RangeError: Maximum call stack size exceeded",
// white-screening the page for paid ad traffic (observed in prod, 2026-06-19). We cannot patch the
// embedded browser, but we can stop depending on its native fragment scroll: intercept the click and
// run a controlled `scrollIntoView` ourselves.
//
// Scope is intentionally opt-in via `data-scroll-target` so this never touches the many other
// same-page hash anchors in the app (e.g. discussion `#reply` / argument-highlight deep links that
// rely on `hashchange`). Only anchors that explicitly mark themselves are enhanced.

let inPageAnchorScrollInstalled = false;

function findMarkedAnchor(target: EventTarget | null): HTMLAnchorElement | null {
    if (!(target instanceof Element)) {
        return null;
    }
    return target.closest("a[data-scroll-target]") as HTMLAnchorElement | null;
}

function handleInPageAnchorClick(event: MouseEvent): void {
    // Mirror the browser's own "only plain left-clicks navigate" rules: let the browser handle
    // modified clicks (open-in-new-tab etc.) untouched. Shared predicate since 2026-09-07 (`FA-R2`);
    // the two-statement spelling this replaced is exactly the shape a line-level scan could not see.
    if (!isPageInterceptableClick(event)) {
        return;
    }

    const anchor = findMarkedAnchor(event.target);
    if (!anchor) {
        return;
    }

    const targetId = anchor.dataset.scrollTarget;
    if (!targetId) {
        return;
    }

    const destination = document.getElementById(targetId);
    if (!destination) {
        // Section isn't on this page — leave the native behavior alone rather than swallow the click.
        return;
    }

    // Take over from the native fragment scroll (the path that overflows in GSA).
    event.preventDefault();

    // r212ab owed-2 (`POLICY-CONTACT-63`): the policy page's sticky Contents `<details>` stayed open after a row was
    // followed, and its in-flow `max-h-[60svh]` panel covered the heading the page had scrolled to. A disclosure that
    // opts in with `data-closes-on-scroll-target` is closed HERE, before `scrollIntoView`, so the destination is
    // computed against the closed layout and lands at its own `scroll-margin-top` below the closed control. Opt-in, so
    // no other disclosure holding a marked anchor changes. Focus moves to the destination below, so nothing focused is
    // left inside the closed panel.
    const owningDisclosure = anchor.closest("details[data-closes-on-scroll-target]");
    if (owningDisclosure instanceof HTMLDetailsElement && owningDisclosure.open) {
        owningDisclosure.open = false;
    }

    try {
        destination.scrollIntoView({
            behavior: prefersReducedMotion() ? "auto" : "smooth",
            block: "start",
        });
    } catch {
        // Older engines may reject the options object — fall back to a plain instant jump.
        try {
            destination.scrollIntoView();
        } catch {
            // ignore: scrolling is a progressive enhancement here
        }
    }

    // `LANDING-44`: move FOCUS to the destination too, not just the viewport.
    //
    // A plain `<a href="#…">` sets the browser's sequential-focus navigation starting point on the
    // target for free. Suppressing the native fragment navigation above (which we must — it is the
    // path that white-screens the iOS Google in-app browser) takes that away with it, and the focus
    // half was never added beside the scroll. The result: a sighted mouse user watches the page glide
    // to the section, while a keyboard user's focus ring is still on the button they left and a screen
    // reader announces nothing at all — the page appears not to have responded, to the group most
    // likely to need a second path.
    //
    // `preventScroll: true` is load-bearing: this handler owns the scroll, and a focus call that
    // re-scrolls would hand it back to the engine. `tabindex` is only added when the destination has
    // none, so a section that is already focusable (or carries a deliberate value) is left alone.
    //
    // This is NOT a return to native fragment navigation: no `href` is assigned, no `location.hash` is
    // written, and no `hashchange` is fired. Focusing an element is a different code path from the one
    // that overflows in GSA.
    try {
        if (!destination.hasAttribute("tabindex")) {
            destination.setAttribute("tabindex", "-1");
        }
        destination.focus({ preventScroll: true });
    } catch {
        // ignore: a focus failure must not swallow the history push below
    }

    // Keep the URL shareable and back-button consistent. We push rather than assigning `location.hash`
    // on purpose: assigning the hash would re-run the native fragment scroll we just replaced (and
    // reintroduce the GSA overflow), whereas a pushState updates the address bar without scrolling or
    // firing `hashchange`.
    //
    // This used to push `window.history.state` verbatim, which DUPLICATED the previous entry — same
    // navigationId, same stored scroll offset — so Back to the anchor entry restored the other entry's
    // position. `pushInPageHistoryEntry` mints a fresh identity, stamps the outgoing entry's live scroll,
    // and marks both as owned by this page instance, so Back is handled in-page instead of re-fetching
    // the whole document.
    try {
        const newUrl = `${window.location.pathname}${window.location.search}#${targetId}`;
        pushInPageHistoryEntry(newUrl);
    } catch {
        // ignore history failures (e.g. sandboxed contexts)
    }

    // FI-4035: the smooth scroll's destination is computed once, and content above the target can still change height
    // during the glide (devo: the anchor ended 89 px below its margin). Once the glide stops, the one scroll writer
    // holds the target at its own `scroll-margin-top` while the layout settles.
    holdInPageAnchorScroll(targetId);
}

export function setupInPageAnchorScroll(): void {
    if (inPageAnchorScrollInstalled || typeof window === "undefined" || typeof document === "undefined") {
        return;
    }
    document.addEventListener("click", handleInPageAnchorClick);
    inPageAnchorScrollInstalled = true;
}

export function resetInPageAnchorScrollForTests(): void {
    if (typeof document !== "undefined") {
        document.removeEventListener("click", handleInPageAnchorClick);
    }
    inPageAnchorScrollInstalled = false;
}
